import type { LevelInput } from '@lp/contracts';
import { Plus, Trash2 } from 'lucide-react';
import { useState } from 'react';
import { useT } from '@/lib/i18n';
import { Button, Input } from './ui';

/** the levels a template gives a new company — which may sit under which is checked by the server (no loop) */
export function LevelsEditor({ levels, onChange }: { levels: LevelInput[]; onChange: (l: LevelInput[]) => void }) {
  const t = useT();
  const [f, setF] = useState<LevelInput>({ code: '', nameEn: '', nameKm: '', canSell: false, allowedUnder: [null] });
  const toggle = (u: string | null) => setF({ ...f, allowedUnder: f.allowedUnder.includes(u) ? f.allowedUnder.filter((x) => x !== u) : [...f.allowedUnder, u] });
  return (
    <div className="space-y-3">
      {!levels.length ? (
        <p className="text-sm text-ink-500">{t('None — the company makes its own levels.')}</p>
      ) : (
        <ul className="space-y-1 text-sm">
          {levels.map((l) => (
            <li key={l.code} className="flex items-center gap-2">
              <span className="font-medium">{l.nameEn}</span> <span className="font-mono text-[11px] text-ink-500">{l.code}</span>
              <span className="text-xs text-ink-500">
                {t('Under')}: {l.allowedUnder.map((u) => u ?? t('the company')).join(', ')}
              </span>
              {l.canSell && <span className="text-xs text-emerald-700">{t('Sells')}</span>}
              <Button variant="ghost" icon={<Trash2 size={12} />} onClick={() => onChange(levels.filter((x) => x.code !== l.code))} className="h-7 px-1" aria-label={`${t('Remove')} ${l.code}`} />
            </li>
          ))}
        </ul>
      )}
      <div className="space-y-2 border-t border-ink-100 pt-3">
        <div className="flex flex-wrap gap-2">
          <Input value={f.code} onChange={(e) => setF({ ...f, code: e.target.value.toUpperCase() })} placeholder={t('Code')} className="w-28" maxLength={20} />
          <Input value={f.nameEn} onChange={(e) => setF({ ...f, nameEn: e.target.value })} placeholder={t('Name (English)')} className="w-40" />
          <Input value={f.nameKm} onChange={(e) => setF({ ...f, nameKm: e.target.value })} placeholder={t('Name (Khmer)')} className="w-40" />
          <label className="flex items-center gap-1.5 text-xs">
            <input type="checkbox" checked={f.canSell} onChange={(e) => setF({ ...f, canSell: e.target.checked })} />
            {t('Sells')}
          </label>
        </div>
        <div className="flex flex-wrap items-center gap-3 text-xs">
          <span className="text-ink-500">{t('May sit under')}:</span>
          {[null, ...levels.map((l) => l.code)].map((u) => (
            <label key={u ?? '-'} className="flex items-center gap-1">
              <input type="checkbox" checked={f.allowedUnder.includes(u)} onChange={() => toggle(u)} />
              {u ?? t('the company')}
            </label>
          ))}
        </div>
        <Button
          variant="secondary"
          icon={<Plus size={14} />}
          onClick={() => {
            onChange([...levels, f]);
            setF({ code: '', nameEn: '', nameKm: '', canSell: false, allowedUnder: [null] });
          }}
          disabled={!f.code || !f.nameEn || !f.nameKm || !f.allowedUnder.length || levels.some((l) => l.code === f.code)}
        >
          {t('Add the level')}
        </Button>
      </div>
    </div>
  );
}
